import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { Icon } from "@iconify/react";
import { Dialog } from "@mui/material";
import * as XLSX from "xlsx";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { CircleX, Download, Upload } from "lucide-react";
import {
  useGetArticleDescription,
  useGetInvArticleDescription,
  useUpdateArticleDescription,
  useUpdateInvArticleDescription,
} from "../../queries/investorPortal";
import { useAuthStore } from "../../store";
import { useRightsStore } from "../../store/rightsStore";
import BCHeader from "../../shared/BCHeader";
import { Box, HStack } from "../../components/utils";
import { sort } from "../../utils/arrays";
import Loader from "../../shared/Loader";
import { EmptyTable } from "../../components/shared/EmptyTable";
import { useGetCompanyDetails } from "../../queries/termsheet";
import { queryClient } from "../../queries";
import {
  ButtonSize,
  IconCTAButton,
  IconSize,
  PrimaryCTAButton,
} from "../quickRound/CTAButtonComponents";
import Tooltip from "../../components/shared/Tooltip";
import { parseRTFToHTML } from "../../modals/ArticleModal";

interface ArticleData {
  id: string;
  articleNumber: string;
  articleDescription: string;
  isEditable?: boolean;
  isExpanded?: boolean;
  articlesData?: any;
}

export interface ArticleModel {
  rightsId?: string;
  cin?: string;
  companyId?: string;
  articleUrl?: string;
  article: ArticleData[];
}

const RightsArticle = () => {
  const { cin, id, token } = useParams();
  const { accessToken } = useAuthStore();
  const navigate = useNavigate();
  const rightsId = useRightsStore.getState().rightsId || id;
  const [data, setData] = useState<ArticleData[]>([]);
  const companyId = useAuthStore.getState().companyId || "";
  const { data: nodeCompanyDetail } = useGetCompanyDetails();
  const cinNumber = nodeCompanyDetail?.companyDetails.cinNumber || "";
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [showFilePopup, setSHowFilePopup] = useState<boolean>(false);
  const [file, setFile] = useState<File>();
  const [rtfText, setRtfText] = useState("");
  const [htmlText, setHtmlText] = useState("");
  const featureHidden = false;

  const handleDoubleClick = (index: number) => {
    handleExpandRow(index);
    setData((prevState) =>
      prevState.map((row, idx) => ({
        ...row,
        isEditable: index === idx ? !row.isEditable : row.isEditable,
      }))
    );
  };

  useEffect(() => {
    const html = parseRTFToHTML(rtfText);
    setHtmlText(html);
  }, [rtfText]);

  const { data: articleData } = cin
    ? useGetInvArticleDescription(rightsId, companyId, cin)
    : useGetArticleDescription(rightsId, companyId, cinNumber);

  useEffect(() => {
    if (articleData?.article) {
      const sortedArray = sortArticle(articleData.article);
      setData(sortedArray);
    }
  }, [articleData]);

  const sortArticle = (article: ArticleData[]) => {
    const sortedArray = article.sort((a, b) => {
      const parts1 = a.articleNumber.split(".").map(Number);
      const parts2 = b.articleNumber.split(".").map(Number);
      for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
        const part1 = parts1[i] || 0;
        const part2 = parts2[i] || 0;

        if (part1 < part2) return -1;
        if (part1 > part2) return 1;
      }
      return 0;
    });
    return sortedArray;
  };

  const { mutate: updateInvArticleDescription } = cin
    ? useUpdateInvArticleDescription()
    : useUpdateArticleDescription();

  const handleExpandRow = (index: number) => {
    setData((prevState) =>
      prevState.map((row, idx) => ({
        ...row,
        isExpanded: index === idx ? !row.isExpanded : row.isExpanded,
        isEditable: index === idx ? row.isEditable : false,
      }))
    );
  };

  const handleInputArticleNumberChange = (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target;
    setData((prevState) =>
      prevState.map((row, idx) => ({
        ...row,
        articleNumber: index === idx ? value : row.articleNumber.trim(),
        isEditable: index === idx,
        isExpanded: index === idx,
      }))
    );
  };

  const handleAddEntry = () => {
    setData((prevState) => [
      ...prevState,
      {
        id: `${Math.floor(Math.random() * 10) + 1}`,
        articleNumber: "",
        articleDescription: "",
        isEditable: true,
      },
    ]);
  };

  const handleDelete = (index: number) => {
    setData((prevState) => prevState.filter((_, idx) => idx !== index));
  };

  const handleInputArticleDescriptionChange = (
    index: number,
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const { value } = event.target;
    setData((prevState) =>
      prevState.map((row, idx) => ({
        ...row,
        articleDescription: index === idx ? value : row.articleDescription,
        isEditable: index === idx,
        isExpanded: index === idx,
      }))
    );
  };

  const handleSubmit = () => {
    let error = "";
    data.forEach((element) => {
      if (!element.articleNumber) error = "Article Number cant be Empty";
    });
    if (error === "") setLoading(true);
    updateInvArticleDescription(
      {
        article: {
          rightsId,
          companyId,
          cin: !cin ? cinNumber : cin,
          article: data,
        },
        token,
      },
      {
        onSuccess: () => {
          setLoading(false);
          toast("Article Updated Successfully!", {
            type: "success",
            autoClose: 2000,
          });
          queryClient.invalidateQueries("getInvArticleDescription");
          queryClient.invalidateQueries("getArticleDescription");
        },
        onError: () => {
          setLoading(false);
          toast("Something went wrong", { type: "error", autoClose: 2000 });
        },
      }
    );
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const filteredData = data.filter(
    (row) =>
      row.articleNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.articleDescription.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleFileUpload = async () => {
    if (file && file !== undefined) {
      const baseUrl =
        process.env.REACT_APP_NEW_CAPTABLE_API ||
        "https://dev-captable-api.hissa.com/";
      const formData = new FormData();
      formData.append("file", file);
      formData.append("rightsId", rightsId || "");
      await fetch(`${baseUrl}v1/company/investorRights/uploadArticlesExcel`, {
        method: "PUT",
        headers: {
          accessToken: accessToken || "",
          token: token || "",
          companyId: companyId || "",
          cin: cin || "",
          rightsId: rightsId || "",
        },
        body: formData,
      })
        .then(async (res) => {
          if (res.status === 200) {
            return res.json();
          } else {
            const errorBody = await res.json();
            toast(
              errorBody.errorMessage ||
                "Error uploading the file, Please verify and try again.",
              {
                type: "error",
                autoClose: 2000,
              }
            );
            return undefined;
          }
        })
        .then((data) => {
          if (data) {
            queryClient.invalidateQueries("getInvArticleDescription");
            queryClient.invalidateQueries("getArticleDescription");
            toast("Article Updated Successfully!", {
              type: "success",
              autoClose: 2000,
            });
            setSHowFilePopup(false);
            setFile(undefined);
          }
        })
        .catch((err) => {
          toast("Error uploading the file.", {
            type: "error",
            autoClose: 2000,
          });
        });
    }
  };

  function FileUploadPopup() {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white mx-6 md:mx-0 rounded-lg shadow-lg w-[450px] space-y-4 relative">
          <Box className="text-lg font-medium border-b py-4 px-6 mb-0">
            <h6 className="flex justify-between">
              {"Upload File"}
              <XMarkIcon
                className="h-5 mt-1 font-medium cursor-pointer"
                onClick={() => {
                  setSHowFilePopup(false);
                  setFile(undefined);
                }}
              />
            </h6>
          </Box>
          <div className="p-6 space-y-4 pt-0">
            {file ? (
              <div className="flex justify-center mt-2">
                <div className="relative w-36 h-32 border rounded-lg overflow-visible bg-gray-50 p-2 flex flex-col items-center justify-center">
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/3/34/Microsoft_Office_Excel_%282019%E2%80%93present%29.svg"
                    alt="Excel File"
                    className="w-20 h-20 mb-2"
                  />

                  <p
                    className="text-xs text-center truncate cursor-pointer w-full px-2"
                    title={file.name}
                  >
                    {file.name}
                  </p>

                  <button
                    onClick={() => setFile(undefined)}
                    className="absolute -top-1 -right-2 text-xs
                    text-red-500 rounded-full px-1 leading-none"
                  >
                    <CircleX
                      height={16}
                      width={16}
                      className="bg-red-100 rounded-lg"
                    />
                  </button>
                </div>
              </div>
            ) : (
              <div className="h-32"></div>
            )}

            <div>
              <div
                onClick={handleUploadClick}
                className={`w-full flex items-center text-base justify-center font-semibold hover:border-primary cursor-pointer hover:text-gray-700 rounded border-2 border-dashed border-gray-300 py-9 gap-2 `}
              >
                <Icon
                  aria-hidden="true"
                  icon={"material-symbols:upload-rounded"}
                  className={""}
                  height={IconSize.M}
                  width={IconSize.M}
                />
                Click to select excel file
              </div>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                accept=".xls,.xlsx"
                onChange={handleExcelUpload}
              />
            </div>

            <div className="flex justify-between gap-2">
              <button
                onClick={() => {
                  setSHowFilePopup(false);
                  setFile(undefined);
                }}
                className="px-4 py-2 text-sm text-gray-600 border rounded hover:bg-gray-100"
              >
                Cancel
              </button>
              <PrimaryCTAButton
                className="w-28 text-center px-auto "
                buttonSize={ButtonSize.SMALL}
                // loading={loading}
                disabled={!file}
                onClick={() => handleFileUpload()}
                type="button"
              >
                <p className="w-full text-sm leading-0"> Upload </p>
              </PrimaryCTAButton>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleExportToExcel = () => {
    if (articleData && articleData.articleUrl) {
      window.open(articleData.articleUrl, "");
    } else {
      const wb = XLSX.utils.book_new();
      const wsData = data.map((row) => ({
        "Article Number": row.articleNumber,
        "Article Description": row.articleDescription,
      }));
      const ws = XLSX.utils.json_to_sheet(wsData);
      XLSX.utils.book_append_sheet(wb, ws, "Articles");
      XLSX.writeFile(wb, `Articles_${rightsId}.xlsx`);
    }
  };

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setFile(file);
    }
  };

  const handleUploadClick = () => {
    const fileInput: any = fileInputRef.current;
    if (fileInput) fileInput.click();
  };

  const handleExcelUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && /\.(xls|xlsx)$/i.test(file.name)) {
      setFile(file);
    } else {
      toast(
        "Invalid file type. Please upload a valid Excel file (.xls or .xlsx).",
        { type: "error", autoClose: 2000 }
      );
    }
  };

  return (
    <div>
      {!cin && (
        <BCHeader
          className="items-baseline mb-2"
          bcTitle="Rights Shareholder Mapping"
          bcSubTitle="Articles"
        />
      )}

      {!articleData ? (
        <Box className="my-auto bg-white rounded-lg h-full w-full shadow-box">
          <Loader />
        </Box>
      ) : (
        <div className="w-full px-8 py-6 bg-white">
          <Dialog open={showFilePopup} maxWidth="md">
            <FileUploadPopup />
          </Dialog>
          <div className="flex justify-between items-center mb-4 ">
            <div className="text-xl font-bold text-[#464E5F]">ARTICLE</div>
            <div className="flex items-center gap-4">
              <Tooltip text={"Download"}>
                <Download
                  className="text-green-600 hover:text-green-700 cursor-pointer w-6 h-6 mt-2"
                  onClick={() => {
                    handleExportToExcel();
                  }}
                />
              </Tooltip>
              <input
                type="text"
                placeholder="Search..."
                className="p-2 border border-gray-300 bg-slate-50 rounded-md"
                onChange={handleSearch}
              />
              {featureHidden && (
                <button
                  className="border-orange-501 border text-orange-501 font-medium py-2 px-4 rounded"
                  onClick={handleAddEntry}
                >
                  Add New Entry
                </button>
              )}
              {!cin && (
                <button
                  className="border-orange-501 text-orange-501 border font-semibold py-1.5 px-3 rounded"
                  data-event-name="Back From Article"
                  onClick={() => {
                    navigate("/rights/shareholder");
                  }}
                >
                  Back
                </button>
              )}
              <button
                className="border-orange-501 text-orange-501 border font-semibold py-1.5 px-3 rounded flex flex-row gap-1"
                data-event-name="Back From Definitions"
                onClick={() => setSHowFilePopup(true)}
              >
                <Upload className="text-primary cursor-pointer w-4 h-4 mt-0.5" />
                <p>Upload</p>
              </button>
              <button
                className="bg-orange-501 text-white font-semibold py-2 px-4 rounded"
                onClick={handleSubmit}
              >
                {`${loading ? "Saving..." : "Save"}`}
              </button>
            </div>
          </div>
          <div className="flex flex-col justify-between items-center">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50">
                  <th className="p-2 w-32 border font-semibold border-gray-300 text-[#464E5F]">
                    Article Number
                  </th>
                  <th className="p-2 flex-1 border font-semibold border-gray-300 text-[#464E5F]">
                    Article Description
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((row, index) => (
                  <>
                    <tr
                      key={row.id}
                      className={
                        index % 2
                          ? "bg-slate-50 hover:bg-slate-100"
                          : "bg-inherit  hover:bg-slate-100"
                      }
                    >
                      <td
                        className={`p-2 w-32 border border-gray-300 ${
                          row.isEditable ? "bg-white" : "cursor-pointer"
                        }`}
                        onClick={() => handleExpandRow(index)}
                      >
                        {row.isEditable ? (
                          <input
                            type="text"
                            name="articleNumber"
                            value={row.articleNumber}
                            onChange={(event) =>
                              handleInputArticleNumberChange(index, event)
                            }
                            className="w-full"
                          />
                        ) : (
                          row.articleNumber
                        )}
                      </td>
                      <td
                        className={`p-2 flex-1 border border-gray-300 ${
                          row.isEditable ? "bg-white" : "cursor-pointer"
                        }`}
                      >
                        {row.isEditable ? (
                          <>
                            <textarea
                              name="articleDescription"
                              value={row.articleDescription}
                              onChange={(event) =>
                                handleInputArticleDescriptionChange(
                                  index,
                                  event
                                )
                              }
                              rows={4}
                              className="w-full resize-y border border-gray-300"
                            />
                          </>
                        ) : (
                          <>
                            <div
                              onClick={() => {
                                handleExpandRow(index);
                                if (row.articlesData?.length > 1) {
                                  setRtfText(
                                    row.articlesData[0]?.articleDescription ||
                                      row.articleDescription
                                  );
                                }
                              }}
                              className={`w-full overflow-hidden text-ellipsis ${
                                row.articlesData?.length > 1 ? "h-10" : "h-6"
                              }`}
                              style={{
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                              }}
                            >
                              {row.articlesData?.length > 1 ? (
                                <div className="space-y-1">
                                  {row.articlesData.map(
                                    (item: any, idx: number) => {
                                      const html = parseRTFToHTML(
                                        item.articleDescription
                                      );
                                      return (
                                        <div
                                          key={idx}
                                          className="break-words w-full"
                                        >
                                          {row.articlesData.length > 1 &&
                                            html !== "" && (
                                              <span className="font-bold">
                                                {item.articleNumber}:{" "}
                                              </span>
                                            )}
                                          <span
                                            dangerouslySetInnerHTML={{
                                              __html: html,
                                            }}
                                          />
                                        </div>
                                      );
                                    }
                                  )}
                                </div>
                              ) : (
                                <div
                                  className="break-words w-full"
                                  dangerouslySetInnerHTML={{
                                    __html: parseRTFToHTML(
                                      row.articlesData[0]?.articleDescription ||
                                        row.articleDescription
                                    ),
                                  }}
                                />
                              )}
                            </div>
                          </>
                        )}
                      </td>
                      {/* <td className="w-16  border border-gray-300">
                        <HStack className="justify-evenly">
                          {row.isEditable ? (
                            <Icon
                              icon="mdi:tick"
                              className="hover:cursor-pointer text-orange-501 scale-125 rounded"
                              height={18}
                              width={18}
                              onClick={() => handleDoubleClick(index)}
                            />
                          ) : (
                            <Icon
                              icon="mi:edit"
                              className="hover:cursor-pointer text-orange-501 scale-125 rounded"
                              height={18}
                              width={18}
                              onClick={() => handleDoubleClick(index)}
                            />
                          )}
                          {featureHidden && (
                            <Icon
                              icon="mi:delete"
                              onClick={() => handleDelete(index)}
                              className={`hover:cursor-pointer text-orange-501 scale-125 rounded `}
                              height={18}
                              width={18}
                            />
                          )}
                        </HStack>
                      </td> */}
                    </tr>

                    {row.isExpanded && (
                      <tr>
                        <td
                          colSpan={3}
                          className="p-2 bg-orange-50 border border-gray-300"
                        >
                          {row.articlesData?.length > 1 ? (
                            <div className="space-y-1">
                              {row.articlesData.map(
                                (item: any, idx: number) => {
                                  const html = parseRTFToHTML(
                                    item.articleDescription
                                  );
                                  return (
                                    <div
                                      key={idx}
                                      className={`w-full break-words ${
                                        idx > 0 && idx !== row.articlesData.length && html !== ""
                                          ? "border-t border-dashed border-gray-400 py-2"
                                          : ""
                                      }`}
                                    >
                                      {row.articlesData.length > 1 &&
                                        html !== "" && (
                                          <span className="font-bold">
                                            {item.articleNumber}:{" "}
                                          </span>
                                        )}
                                      <span
                                        dangerouslySetInnerHTML={{
                                          __html: html,
                                        }}
                                      />
                                    </div>
                                  );
                                }
                              )}
                            </div>
                          ) : (
                            <div
                              dangerouslySetInnerHTML={{
                                __html: parseRTFToHTML(
                                  row.articlesData[0]?.articleDescription ||
                                    row.articleDescription
                                ),
                              }}
                            />
                          )}
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
            {filteredData.length === 0 && (
              <EmptyTable header="No Article Found" subHeader="" />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RightsArticle;
